import React, { useState, forwardRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Alert,
  Snackbar,
  useTheme,
  Slide,
  Divider,
  CircularProgress,
  Portal,
  Fade,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { tokens } from "../../theme";
import { createProduct, updateProduct } from "../../services/ProductService";
import { Formik } from "formik";
import * as yup from "yup";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const validationSchema = yup.object().shape({
  productName: yup.string().required("Product Name is required"),
  category: yup.string().required("Category is required"),
  productDesc: yup.string().required("Product Description is required"),
  sourceType: yup.string().required("Source Type is required"),
  supplierName: yup.string().required("Supplier Name is required"),
  storeLocation: yup.string().required("Location is required"),
  cost: yup
    .number()
    .typeError("Cost must be a valid number")
    .min(0, "Cost cannot be negative")
    .required("Unit Cost Price is required"),
  quantity: yup
    .number()
    .typeError("Quantity must be a valid number")
    .min(0, "Quantity cannot be negative")
    .required("Quantity is required"),
});

const ProductForm = ({
  open,
  handleClose,
  onProductAdded,
  onProductEdited,
  editProduct,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const editMode = Boolean(editProduct);

  const [phase, setPhase] = useState("idle");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const initialValues = {
    productName: editProduct?.productName || "",
    productDesc: editProduct?.productDesc || "",
    category: editProduct?.category || "",
    sourceType: editProduct?.sourceType || "",
    supplierName: editProduct?.supplierName || "",
    storeLocation: editProduct?.storeLocation || "",
    cost: editProduct?.cost || "",
    // Default to 0 instead of "" for the frontend failsafe
    quantity: editProduct?.quantity ?? 0,
  };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setPhase("loading");
    return new Promise(async (resolve) => {
      try {
        let response;
        if (editMode) {
          response = await updateProduct(editProduct.id, values);
        } else {
          response = await createProduct(values);
        }

        // Keep loading state visible for 1.5 seconds to match other forms
        setTimeout(() => {
          setPhase("idle");
          resetForm();
          handleClose();

          // Refresh the table
          if (editMode) {
            onProductEdited(response.data);
          } else {
            onProductAdded(response.data);
          }

          // Show the snackbar slightly after the dialog closes
          setTimeout(() => {
            setSnackbarSeverity("success");
            setSnackbarMessage(
              `Product ${editMode ? "updated" : "added"} successfully!`,
            );
            setSnackbarOpen(true);
            resolve();
          }, 300);
        }, 1500);
      } catch (error) {
        console.error(
          `Failed to ${editMode ? "update" : "create"} product: `,
          error,
        );
        setSnackbarSeverity("error");
        setSnackbarMessage(
          `Failed to ${editMode ? "update" : "create"} product. Please try again.`,
        );
        setPhase("idle");
        setSnackbarOpen(true);
        resolve();
      }
    });
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Shared input styling to match the modern aesthetic
  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        display: "none",
        WebkitAppearance: "none",
        margin: 0,
      },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  };

  const dropdownPaperStyle = {
    backgroundColor:
      theme.palette.mode === "dark"
        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
        : "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(16px)",
    border: `1px solid ${
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.15)"
        : "rgba(0, 0, 0, 0.1)"
    }`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
    borderRadius: "12px",
    mt: 1,
    "& .MuiMenuItem-root": {
      padding: "12px 20px",
      margin: "4px 8px",
      borderRadius: "8px",
      transition: "all 0.2s ease-in-out",
      color: colors.gray[100],
      fontWeight: 600,
      fontSize: "14px",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
      "&:active": { transform: "scale(0.95)" },
      "&.Mui-selected": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(0, 0, 0, 0.08)",
        color: colors.blueAccent[500],
      },
      "&.Mui-selected:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.12)",
      },
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (phase !== "loading") handleClose();
        }}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          sx={{ fontSize: "1.25rem", fontWeight: "700", pt: 3 }}
        >
          {editMode ? "Edit Product" : "Add New Product"}
        </DialogTitle>
        <Divider
          sx={{
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          }}
        />
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: "16px !important" }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              setFieldValue,
              handleSubmit,
            }) => (
              <form onSubmit={handleSubmit}>
                <Box
                  display="grid"
                  gap="16px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Product Name"
                    name="productName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.productName}
                    error={!!touched.productName && !!errors.productName}
                    helperText={touched.productName && errors.productName}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Category"
                    name="category"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.category}
                    error={!!touched.category && !!errors.category}
                    helperText={touched.category && errors.category}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Product Description"
                    name="productDesc"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.productDesc}
                    error={!!touched.productDesc && !!errors.productDesc}
                    helperText={touched.productDesc && errors.productDesc}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <FormControl
                    fullWidth
                    variant="filled"
                    error={!!touched.sourceType && !!errors.sourceType}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                  >
                    <InputLabel
                      id="source-type-label"
                      style={{ fontSize: "0.95rem" }}
                    >
                      Source
                    </InputLabel>
                    <Select
                      labelId="source-type-label"
                      name="sourceType"
                      value={values.sourceType}
                      onChange={(event) => {
                        const value = event.target.value;
                        setFieldValue("sourceType", value);

                        if (value === "Manufactured") {
                          setFieldValue("supplierName", "In-House");
                        }
                      }}
                      onBlur={handleBlur}
                      disableUnderline
                      sx={{ borderRadius: "8px", fontSize: "0.95rem" }}
                      MenuProps={{
                        TransitionComponent: Fade,
                        transitionDuration: 300,
                        PaperProps: { sx: dropdownPaperStyle },
                      }}
                    >
                      <MenuItem value="Manufactured">Manufactured</MenuItem>
                      <MenuItem value="Purchased">Purchased</MenuItem>
                    </Select>
                    {touched.sourceType && errors.sourceType && (
                      <FormHelperText>{errors.sourceType}</FormHelperText>
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Supplier"
                    name="supplierName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.supplierName}
                    error={!!touched.supplierName && !!errors.supplierName}
                    helperText={touched.supplierName && errors.supplierName}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Location"
                    name="storeLocation"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.storeLocation}
                    error={!!touched.storeLocation && !!errors.storeLocation}
                    helperText={touched.storeLocation && errors.storeLocation}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Unit Cost Price"
                    name="cost"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.cost}
                    error={!!touched.cost && !!errors.cost}
                    helperText={touched.cost && errors.cost}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Quantity"
                    name="quantity"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.quantity}
                    error={!!touched.quantity && !!errors.quantity}
                    helperText={touched.quantity && errors.quantity}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 4", sm: "span 2" },
                    }}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />
                </Box>

                <Divider
                  sx={{
                    mt: 3,
                    mb: 0,
                    mx: { xs: -2, sm: -3 },
                    borderColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.05)",
                  }}
                />

                <DialogActions
                  sx={{
                    pt: 2,
                    pb: 0,
                    px: 0,
                    gap: 1,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    onClick={handleClose}
                    disabled={phase === "loading"}
                    variant="outlined"
                    sx={{
                      color: colors.gray[100],
                      borderColor: colors.gray[500],
                      textTransform: "none",
                      fontWeight: "600",
                      padding: "10px 28px",
                      borderRadius: "8px",
                      width: { xs: "100%", sm: "auto" },
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        borderColor: colors.gray[300],
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.04)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={phase === "loading"}
                    variant="contained"
                    sx={{
                      backgroundColor: colors.blueAccent[600],
                      color: "#ffffff",
                      textTransform: "none",
                      fontWeight: "600",
                      padding: "10px 32px",
                      borderRadius: "8px",
                      width: { xs: "100%", sm: "auto" },
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 4px 10px rgba(0,0,0,0.3)"
                          : "0 4px 12px rgba(79, 70, 229, 0.2)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor: colors.blueAccent[500],
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 6px 14px rgba(0,0,0,0.4)"
                            : "0 6px 16px rgba(79, 70, 229, 0.3)",
                        transform: "translateY(-1px)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.12)"
                            : "rgba(0, 0, 0, 0.12)",
                        color:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.3)"
                            : "rgba(0, 0, 0, 0.26)",
                      },
                    }}
                  >
                    {phase === "loading" ? (
                      <>
                        Saving&nbsp;
                        <CircularProgress
                          size={18}
                          sx={{ color: "inherit", ml: "8px" }}
                        />
                      </>
                    ) : editMode ? (
                      "Update"
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </DialogActions>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#fff",
              fontWeight: "600",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default ProductForm;
